import React from 'react';
import FavoriteBuildButton from './FavoriteBuildButton';
import LikeBuildButton from './LikeBuildButton';
import VehicleEngagementMeta from './VehicleEngagementMeta';
import { getBuildLevel, isVehicleCertified } from '../utils/buildLevel';
import { DiamondScoreBadge } from './ui/DiamondScoreBadge';
import CertifiedBuildSeal from './ui/CertifiedBuildSeal';
import ExteriorFinishDisplay from './vehicle/ExteriorFinishDisplay';
import { getExteriorFinishPresentation } from '../utils/exteriorFinish';
import { VEHICLE_COLLECTIBILITY_BADGE } from './ui/vehicleBadgeStyles';
import { BadgeStack } from './badges/BadgeRenderer';
import type { IssuedBadge } from '../types/discoveryBadges';
import { resolvePublicFileUrl } from '../utils/imageUrls';
import './VehicleCard.css';

interface VehicleCardProps {
  vehicle: {
    id: number;
    year: number;
    make: string;
    model: string;
    trim: string;
    color?: string;
    exterior_finish_type?: 'paint' | 'wrap' | 'ppf';
    exterior_finish_color?: string | null;
    exterior_finish_brand?: string | null;
    exterior_finish_style?: string | null;
    exterior_finish_label?: string | null;
    nickname?: string;
    description?: string;
    modification_count: number;
    last_modification?: string;
    display_name: string;
    owner_username?: string;
    owner_display_name?: string;
    image_url?: string;
    is_primary?: boolean;
    is_public?: boolean;
    created_at: string;
    updated_at?: string;
    chassis?: string | null;
    engine?: string | null;
    drivetrain?: string | null;
    horsepower?: number;
    torque?: number;
    redline?: number;
    settings?: {
      show_pricing?: boolean;
      show_performance?: boolean;
    };
    total_investment?: number;
    like_count?: number;
    user_has_liked?: boolean;
    favorite_count?: number;
    is_favorited?: boolean;
    view_count?: number;
    pp?: number;
    rarity_stars?: number;
    collector_points?: number;
    base_rarity_stars?: number;
    base_collector_points?: number;
    base_rarity_level?: string;
    vehicle_overview?: string;
    production_years?: string;
    production_numbers?: string;
    /** Human-reviewed build truth — see docs/BUILD_CARD_AND_CERTIFICATION.md */
    certification_status?: string | null;
  };
  onVehicleClick: (vehicle: any) => void;
  onUserClick?: (username: string) => void;
  showOwner?: boolean;
  isOwner?: boolean;
  onEditClick?: (vehicle: any) => void;
  completionPercent?: number;
  /** Garage / collection view — calmer card, photo-first (tier frames still apply). */
  collectionMode?: boolean;
  /** Favorite control for public discovery; never on own garage cards. Default: on when not owner. */
  showFavorite?: boolean;
  /** Like control; default same as favorite visibility. */
  showLike?: boolean;
  onFavoriteChange?: (favorited: boolean, favoriteCount: number) => void;
  onLikeChange?: (liked: boolean, likeCount: number) => void;
  discoveryBadges?: IssuedBadge[];
  discoveryBadgeOverflow?: number;
  onBadgeOverflowClick?: () => void;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const VehicleCard: React.FC<VehicleCardProps> = ({
  vehicle,
  onVehicleClick,
  onUserClick,
  showOwner = false,
  isOwner = false,
  onEditClick,
  completionPercent,
  collectionMode = false,
  showFavorite,
  showLike,
  onFavoriteChange,
  onLikeChange,
  discoveryBadges,
  discoveryBadgeOverflow = 0,
  onBadgeOverflowClick,
}) => {
  const buildLevel = getBuildLevel(vehicle.modification_count);
  const certified = isVehicleCertified(vehicle.certification_status);
  const showPendingSeal = isOwner && vehicle.certification_status === 'pending_review';
  const displayFavorite = !isOwner && (showFavorite ?? true);
  const displayLike = !isOwner && (showLike ?? true);

  const showPerformance =
    (isOwner || vehicle.settings?.show_performance !== false) &&
    (vehicle.horsepower || vehicle.torque || vehicle.redline);
  const showPricing =
    (isOwner || vehicle.settings?.show_pricing !== false) &&
    vehicle.total_investment &&
    vehicle.total_investment > 0;

  const getUpdateDate = () => {
    if (vehicle.last_modification) return new Date(vehicle.last_modification);
    if (vehicle.updated_at) return new Date(vehicle.updated_at);
    return new Date(vehicle.created_at);
  };

  const updateDate = getUpdateDate();
  const hasCollectibility =
    vehicle.base_rarity_stars !== undefined && vehicle.base_rarity_stars > 0;

  return (
    <div className="group relative vehicle-card">
      {/* Quiet edge wash — photo stays the hero; no dual blur glow */}
      <div
        className={`pointer-events-none absolute -inset-px rounded-xl bg-gradient-to-br ${buildLevel.color} opacity-[0.1] transition-opacity duration-300 group-hover:opacity-[0.18] vehicle-card-glow`}
        aria-hidden
      />

      <div className={buildLevel.frameClass}>
        <div
          className={`vehicle-card-frame__inner vehicle-card-main transition-all duration-300 ${
            collectionMode ? '' : ''
          }`}
        >
          {buildLevel.foilClass ? (
            <div className={buildLevel.foilClass} aria-hidden />
          ) : null}

          <div className="absolute top-3 right-3 z-20">
            <DiamondScoreBadge modCount={vehicle.modification_count} size="sm" />
          </div>

          {hasCollectibility && (
            <div className="absolute top-3 left-3 z-20">
              <div className={VEHICLE_COLLECTIBILITY_BADGE}>
                <span>{vehicle.base_rarity_stars}★</span>
                {vehicle.base_collector_points !== undefined &&
                  vehicle.base_collector_points > 0 && (
                    <span>{vehicle.base_collector_points} pts</span>
                  )}
              </div>
            </div>
          )}

          {!vehicle.is_public && isOwner && (
            <div
              className={`absolute z-20 ${
                hasCollectibility ? 'top-12 left-3' : 'top-3 left-3'
              }`}
            >
              <div className="bg-[#111]/90 text-gray-300 text-xs font-medium px-2 py-1 rounded-md border border-white/[0.08] shadow-lg shadow-black/25 flex items-center space-x-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                </svg>
                <span>Private</span>
              </div>
            </div>
          )}

          <div
            className={`relative bg-gradient-to-b from-[#141414] to-[#0a0a0a] overflow-hidden ${
              collectionMode ? 'h-52 sm:h-56' : 'h-48'
            }`}
          >
            {vehicle.image_url ? (
              <img
                src={resolvePublicFileUrl(vehicle.image_url) || vehicle.image_url}
                alt={vehicle.display_name}
                className="w-full h-full object-cover cursor-pointer transition-transform duration-500 vehicle-card-image"
                onClick={() => onVehicleClick(vehicle)}
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center cursor-pointer transition-colors vehicle-card-no-image"
                onClick={() => onVehicleClick(vehicle)}
              >
                <div className="text-center">
                  <svg
                    className="w-16 h-16 text-gray-500 mx-auto mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-gray-500 text-sm">No Image</p>
                </div>
              </div>
            )}

            {(certified || showPendingSeal) && (
              <div className="absolute bottom-3 left-3 z-20">
                <CertifiedBuildSeal
                  status={vehicle.certification_status}
                  showPendingForOwner={showPendingSeal}
                />
              </div>
            )}

            {displayLike || displayFavorite ? (
              <div className="absolute bottom-3 right-3 z-20 flex items-center gap-1.5">
                {displayLike ? (
                  <LikeBuildButton
                    vehicleId={vehicle.id}
                    initialLiked={vehicle.user_has_liked || false}
                    initialLikeCount={vehicle.like_count ?? 0}
                    size="sm"
                    showCount
                    onLikeChange={onLikeChange}
                  />
                ) : null}
                {displayFavorite ? (
                  <FavoriteBuildButton
                    vehicleId={vehicle.id}
                    initialFavorited={vehicle.is_favorited || false}
                    initialFavoriteCount={vehicle.favorite_count ?? 0}
                    size="sm"
                    showCount
                    onFavoriteChange={onFavoriteChange}
                  />
                ) : null}
              </div>
            ) : null}
          </div>

          {/* Always reserve badge-rail height so cards without awards/events stay the same height. */}
          <div className="vehicle-card-badge-rail px-3 -mt-3.5 relative z-20 mb-1 min-h-[2rem] flex items-start">
            {discoveryBadges && discoveryBadges.length > 0 ? (
              <div className="inline-flex max-w-full rounded-full bg-[#0a0a0a]/85 backdrop-blur-[2px] border border-white/[0.08] shadow-lg shadow-black/30">
                <BadgeStack
                  badges={discoveryBadges}
                  overflow={discoveryBadgeOverflow}
                  variant="overlap"
                  size="xs"
                  onOverflowClick={onBadgeOverflowClick}
                />
              </div>
            ) : null}
          </div>

          <div className={`p-4 ${collectionMode ? 'bg-[#0a0a0a]' : 'bg-gradient-to-b from-[#141414] to-[#0a0a0a]'}`}>
            <div className="mb-3">
              <div className="flex items-start justify-between gap-2">
                <h3
                  className="text-lg font-bold text-white cursor-pointer transition-colors leading-tight vehicle-card-title"
                  onClick={() => onVehicleClick(vehicle)}
                >
                  {vehicle.display_name}
                </h3>
                {buildLevel.showTierLabel && (
                  <span
                    className="shrink-0 mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 border border-white/[0.08] rounded px-1.5 py-0.5"
                    title={`Build score: ${buildLevel.name}`}
                  >
                    {buildLevel.name}
                  </span>
                )}
              </div>

              {(vehicle.chassis || vehicle.engine || vehicle.drivetrain) && collectionMode ? (
                <p className="text-xs text-gray-500 mt-1 truncate">
                  {[vehicle.chassis, vehicle.engine, vehicle.drivetrain].filter(Boolean).join(' · ')}
                </p>
              ) : null}

              <div className="text-sm text-gray-400 font-medium mt-1.5">
                {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim}
              </div>
            </div>

            {getExteriorFinishPresentation(vehicle) && (
              <div className="mb-3">
                <ExteriorFinishDisplay data={vehicle} compact />
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              <div className="stat-tile p-2">
                <div className="text-sm font-bold text-white">{vehicle.modification_count}</div>
                <div className="text-xs text-gray-400">Mods</div>
              </div>
              <div className="stat-tile p-2">
                <div className="text-sm font-bold text-orange-300 tabular-nums">
                  {showPricing && vehicle.total_investment && vehicle.total_investment > 0
                    ? vehicle.total_investment >= 1000
                      ? `$${(vehicle.total_investment / 1000).toFixed(0)}K`
                      : formatCurrency(vehicle.total_investment)
                    : '-'}
                </div>
                <div className="text-xs text-gray-400">Cost</div>
              </div>
              <div className="stat-tile p-2">
                <div className="text-sm font-bold text-orange-400">
                  {showPerformance && vehicle.horsepower ? `${vehicle.horsepower} HP` : '-'}
                </div>
                <div className="text-xs text-gray-400">
                  {showPerformance && vehicle.torque ? `${vehicle.torque} lb-ft` : '-'}
                </div>
              </div>
              <div className="stat-tile p-2">
                <div className="text-sm font-bold text-red-400">
                  {showPerformance && vehicle.redline ? vehicle.redline.toLocaleString() : '-'}
                </div>
                <div className="text-xs text-gray-400">RPM</div>
              </div>
            </div>

            {typeof completionPercent === 'number' && (
              <div className="mb-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-gray-500">
                    {collectionMode ? 'Profile strength' : 'Profile completeness'}
                  </span>
                  <span
                    className={
                      completionPercent >= 100
                        ? 'text-emerald-400 font-semibold'
                        : 'text-gray-300 font-medium'
                    }
                  >
                    {completionPercent >= 100 ? 'QR ready' : `${completionPercent}%`}
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-[#141414] overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      completionPercent >= 100 ? 'bg-emerald-500' : 'bg-white/80'
                    }`}
                    style={{ width: `${Math.min(100, Math.max(0, completionPercent))}%` }}
                  />
                </div>
                {collectionMode && completionPercent < 100 && (
                  <p className="text-[11px] text-gray-600 mt-1.5">
                    Add specs, a photo, and a mod to get your shareable QR
                  </p>
                )}
              </div>
            )}

            {!collectionMode && (
              <div className="text-xs text-gray-500 mb-3 space-y-1">
                <VehicleEngagementMeta
                  viewCount={vehicle.view_count ?? 0}
                  likeCount={vehicle.like_count ?? 0}
                  favoriteCount={vehicle.favorite_count ?? 0}
                />
                <div className="flex items-center space-x-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                  <span>Updated {updateDate.toLocaleDateString()}</span>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              {showOwner && vehicle.owner_username && onUserClick && (
                <button
                  type="button"
                  onClick={() => onUserClick(vehicle.owner_username!)}
                  className="text-orange-400 hover:text-orange-300 text-sm transition-colors flex items-center space-x-1 font-medium cursor-pointer"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <span>@{vehicle.owner_username}</span>
                </button>
              )}

              <div className="flex space-x-2 ml-auto">
                {isOwner && onEditClick && (
                  <button type="button" onClick={() => onEditClick(vehicle)} className="btn-secondary btn-sm">
                    Edit
                  </button>
                )}
                <button type="button" onClick={() => onVehicleClick(vehicle)} className="btn-primary btn-sm">
                  View build
                </button>
              </div>
            </div>
          </div>

          <div
            className={`absolute inset-0 rounded-xl bg-gradient-to-r ${buildLevel.color} opacity-0 transition-opacity duration-300 pointer-events-none vehicle-card-border`}
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
};

export default VehicleCard;
